import * as Yup from 'yup';

// Define validation rules per type

const validationRules = {
  string: (field, validation) => {
    let schema = Yup.string().nullable();

    if (validation?.required) {
      schema = schema.required(`${field.label} is required`);
    }
    if (validation?.minLength) {
      schema = schema.min(
        validation.minLength,
        `${field.label} must be at least ${validation.minLength} characters`
      );
    }
    if (validation?.maxLength) {
      schema = schema.max(
        validation.maxLength,
        `${field.label} must be at most ${validation.maxLength} characters`
      );
    }
    if (validation?.pattern) {
      schema = schema.test({
        name: 'pattern',
        message:
          validation?.messages?.pattern || `Invalid ${field.label} format`,
        test: value => !value || new RegExp(validation.pattern).test(value)
      });
    }
    return schema;
  },

  number: (field, validation) => {
    let schema = Yup.number()
      .transform(
        (value, originalValue) => (originalValue === '' ? null : value) // Convert "" to null
      )
      .nullable()
      .typeError(`${field.label} must be a number`);

    if (validation?.required) {
      schema = schema.required(`${field.label} is required`);
    }
    if (validation?.minValue !== undefined) {
      schema = schema.min(
        validation.minValue,
        `${field.label} must be at least ${validation.minValue}`
      );
    }
    if (validation?.maxValue !== undefined) {
      schema = schema.max(
        validation.maxValue,
        `Enter ${field.label} that is less than ${validation.maxValue}`
      );
    }
    if (validation?.pattern) {
      schema = schema.test({
        name: 'pattern',
        message:
          validation?.messages?.pattern || `Invalid ${field.label} format`,
        test: value =>
          value === null ||
          value === undefined ||
          (typeof value === 'number' &&
            new RegExp(validation.pattern).test(String(value)))
      });
    }
    return schema;
  },

  date: (field, validation) => {
    let schema = Yup.date()
      .nullable()
      .transform((value, originalValue) => {
        // Return null if empty string or invalid date
        return originalValue === '' || value === 'Invalid Date' ? null : value;
      })
      .typeError(`${field.label} must be a valid date`);

    if (validation?.required) {
      schema = schema.required(`${field.label} is required`);
    }
    if (validation?.pattern) {
      schema = schema.test({
        name: 'pattern',
        message:
          validation?.messages?.pattern || `Invalid ${field.label} format`,
        test: value => !value || new RegExp(validation.pattern).test(value)
      });
    }
    return schema;
  },

  email: (field, validation) => {
    let schema = Yup.string().nullable().email('Invalid email format');

    if (validation?.required) {
      schema = schema.required(`${field.label} is required`);
    }
    if (validation?.pattern) {
      schema = schema.test({
        name: 'pattern',
        message:
          validation?.messages?.pattern || `Invalid ${field.label} format`,
        test: value => !value || new RegExp(validation.pattern).test(value)
      });
    }
    return schema;
  },

  dropdown: (field, validation) => {
    if (field.multiSelect) {
      let schema = Yup.array()
        .nullable()
        .transform((value, originalValue) => originalValue || []);

      if (validation?.required) {
        schema = schema
          .min(1, `Please select at least one ${field.label.toLowerCase()}`)
          .required(`${field.label} is required`);
      }
      return schema;
    }

    let schema = Yup.string().nullable();
    if (validation?.required) {
      schema = schema.required(`${field.label} is required`);
    }
    return schema;
  },

  contact_number: (field, validation) => {
    let schema = Yup.string()
      .nullable()
      .matches(/^\d*$/, 'Phone number must only contain digits');

    if (validation?.required) {
      schema = schema.required(`${field.label} is required`);
    }
    if (validation?.minLength) {
      schema = schema.min(
        validation.minLength,
        `${field.label} must be exactly ${validation.minLength} digits`
      );
    }
    if (validation?.maxLength) {
      schema = schema.max(
        validation.maxLength,
        `${field.label} must be exactly ${validation.maxLength} digits`
      );
    }
    if (validation?.pattern) {
      schema = schema.test({
        name: 'pattern',
        message:
          validation?.messages?.pattern || `Invalid ${field.label} format`,
        test: value => !value || new RegExp(validation.pattern).test(value)
      });
    }
    return schema;
  },
  minmax: (field, validation) => {
    return Yup.object().shape({
      min: Yup.number()
        .transform((value, originalValue) => {
          // Convert empty strings to null, preserve valid numbers including 0
          return originalValue === '' || originalValue === undefined
            ? null
            : value;
        })
        .nullable()
        .typeError(`${field.label} Min must be a number`)
        // Keep min validation if you want values to be ≥ 0
        .min(0, `${field.label} Min must be greater than or equal to 0`)
        .test(
          'both-or-none',
          `${field.label} Min is required if Max is filled`,
          function (value) {
            const { max } = this.parent;
            // Only enforce this rule if max has a value (not null)
            return max === null || max === undefined || value !== null;
          }
        ),

      max: Yup.number()
        .transform((value, originalValue) => {
          // Convert empty strings to null, preserve valid numbers including 0
          return originalValue === '' || originalValue === undefined
            ? null
            : value;
        })
        .nullable()
        .typeError(`${field.label} Max must be a number`)
        // Keep min validation if you want values to be ≥ 0
        .min(0, `${field.label} Max must be greater than or equal to 0`)
        .test(
          'both-or-none',
          `${field.label} Max is required if Min is filled`,
          function (value) {
            const { min } = this.parent;
            // Only enforce this rule if min has a value (not null)
            return min === null || min === undefined || value !== null;
          }
        )
        .test(
          'greater-than-min',
          `${field.label} Max must be greater than Min`,
          function (value) {
            const { min } = this.parent;

            // CRITICAL FIX: Skip validation entirely if either value is null
            // This ensures we don't validate if fields are empty
            if (
              min === null ||
              min === undefined ||
              value === null ||
              value === undefined
            ) {
              return true;
            }

            // Only validate if both min and max have actual values
            return value >= min;
          }
        )
    });
  },

  geo_location: (field, validation) => {
    if (!validation?.required) {
      return Yup.mixed().nullable();
    }

    return Yup.mixed()
      .nullable()
      .test('is-valid-location', `${field.label} is invalid`, value => {
        return (
          value !== null && typeof value === 'object' && !Array.isArray(value)
        );
      })
      .required(`${field.label} is required`);
  },

  coordinates: () => Yup.mixed().nullable(),

  table: field => {
    return Yup.array()
      .of(
        Yup.object().shape(
          field.table.columns.reduce((acc, column) => {
            if (column.validation) {
              acc[column.id] = getFieldValidator(column);
            }
            return acc;
          }, {})
        )
      )
      .min(1, `At least one row is required in ${field.table.title}`);
  },

  toggle_with_dropdown: field => {
    const schema = {
      [field.id]: Yup.string().required('Please select an option').nullable()
    };

    // Add dependent validations for dropdowns
    Object.entries(field.options).forEach(([key, dropdown]) => {
      schema[key] = Yup.string().when(field.id, {
        is: value => value === key,
        then: () =>
          Yup.string()
            .required(`Please select a ${dropdown.label.toLowerCase()}`)
            .nullable(),
        otherwise: () => Yup.string().nullable()
      });
    });

    return schema;
  },

  date_range: (field, validation) => {
    if (!validation?.required) {
      return Yup.mixed().nullable();
    }

    return Yup.mixed()
      .nullable()
      .test('is-valid-date_range', `${field.label} is invalid`, value => {
        return (
          value !== null && typeof value === 'object' && !Array.isArray(value)
        );
      })
      .required(`${field.label} is required`);
  },

  month_day_range: (field, validation) => {
    if (!validation?.required) {
      return Yup.mixed().nullable();
    }

    return Yup.mixed()
      .nullable()
      .test('is-valid-month_day_range', `${field.label} is invalid`, value => {
        return (
          value !== null && typeof value === 'object' && !Array.isArray(value)
        );
      })
      .required(`${field.label} is required`);
  },

  password: (field, validation) => {
    let schema = Yup.string().nullable();

    if (validation?.required) {
      schema = schema.required(`${field.label || 'Password'} is required`);
    }
    schema = schema
      .min(
        validation?.minLength || 8,
        `${field.label || 'Password'} must be at least ${
          validation?.minLength || 8
        } characters`
      )
      .matches(
        /[A-Z]/,
        validation?.messages?.uppercase ||
          `${
            field.label || 'Password'
          } must contain at least one uppercase letter`
      )
      .matches(
        /\d/,
        validation?.messages?.number ||
          `${field.label || 'Password'} must contain at least one number`
      )
      .matches(
        /[!@#$%^&*(),.?":{}|<>]/,
        validation?.messages?.specialCharacter ||
          `${
            field.label || 'Password'
          } must contain at least one special character`
      )
      .oneOf(
        [Yup.ref(validation?.reference || 'password_hash'), null],
        validation?.messages?.match ||
          `${field.label || 'Confirm Password'} must match Password`
      );

    return schema;
  },

  textAndUnit: (field, validation) => {
    const objectShape = {
      // Quantity field validation (string that represents a number)
      quantity: Yup.string()
        .nullable()
        .transform((value, originalValue) => {
          // Clean up any extra quotes
          if (
            typeof originalValue === 'string' &&
            originalValue.startsWith('"') &&
            originalValue.endsWith('"')
          ) {
            return originalValue.slice(1, -1);
          }
          return originalValue;
        })
        .test(
          'is-numeric',
          `${field.label} quantity must be a valid number`,
          value => {
            if (!value || value === '') return true; // Allow empty values
            const cleaned =
              typeof value === 'string' && value.startsWith('"')
                ? value.slice(1, -1)
                : value;
            const parsed = parseFloat(cleaned);
            return !isNaN(parsed) && isFinite(parsed);
          }
        ),

      // Unit field validation (string/dropdown selection)
      unit: Yup.string()
        .nullable()
        .transform((value, originalValue) => {
          // Clean up any extra quotes
          if (
            typeof originalValue === 'string' &&
            originalValue.startsWith('"') &&
            originalValue.endsWith('"')
          ) {
            return originalValue.slice(1, -1);
          }
          return originalValue;
        })
    };

    // Apply required validation if specified
    if (validation?.required) {
      objectShape.quantity = objectShape.quantity.required(
        `${field.label} quantity is required`
      );
      objectShape.unit = objectShape.unit.required(
        `${field.label} unit is required`
      );
    }

    // Apply additional validation rules for quantity if specified
    if (validation?.minValue !== undefined) {
      objectShape.quantity = objectShape.quantity.test(
        'min-value',
        `${field.label} quantity must be at least ${validation.minValue}`,
        value => {
          if (!value || value === '') return true; // Allow empty values
          const cleaned =
            typeof value === 'string' && value.startsWith('"')
              ? value.slice(1, -1)
              : value;
          const numValue = parseFloat(cleaned);
          return !isNaN(numValue) && numValue >= validation.minValue;
        }
      );
    }
    if (validation?.maxValue !== undefined) {
      objectShape.quantity = objectShape.quantity.test(
        'max-value',
        `${field.label} quantity must be less than ${validation.maxValue}`,
        value => {
          if (!value || value === '') return true; // Allow empty values
          const cleaned =
            typeof value === 'string' && value.startsWith('"')
              ? value.slice(1, -1)
              : value;
          const numValue = parseFloat(cleaned);
          return !isNaN(numValue) && numValue <= validation.maxValue;
        }
      );
    }

    let schema = Yup.object().shape(objectShape);

    // Make the entire object required if validation requires it
    if (validation?.required) {
      schema = schema.required(`${field.label} is required`);
    }

    return schema;
  }
};

const getFieldValidator = field => {
  // Special case for fields with specific IDs
  // if (field.id === 'contact_number') {
  //   return validationRules.contact_number(field, field.validation);
  // }

  // Get the appropriate validation rule based on field type
  const validationRule = validationRules[field.type] || validationRules.string;
  return validationRule(field, field.validation);
};

export const generateValidationSchema = ({ fields }) => {
  const schemaFields = {};

  fields.forEach(field => {
    if (field.table) {
      schemaFields[field.id] = validationRules.table(field);
    } else if (field.type === 'toggle_with_dropdown') {
      Object.assign(schemaFields, validationRules.toggle_with_dropdown(field));
    } else if (field.modal) {
      // Object.assign(schemaFields, validationRules.toggle_with_dropdown(field));
    } else {
      schemaFields[field.id] = getFieldValidator(field);
    }
  });

  return Yup.object().shape(schemaFields);
};
